import React, { useCallback, useEffect, useState } from 'react'
import { debounce } from 'lodash'
import SignUpService from '@/app-domains/user/onboarding/signup.service'
import { centsToDollars } from '@/helpers/currency.helper'
import './SignUpThirdStep.sass'
import { getItemFromLocalStorage, saveItemInLocalStorage } from '@/core/localstorage'
import { InputWithSelect } from '@/components/InputWithSelect/InputWithSelect'
import store from '@/core/store'
import { Button } from '@/components/Button/Button'
import { InputMask } from '@/components/InputMask/InputMask'
import { SimpleBanner } from '@/components/Banners/SimpleBanner'
import { ReactComponent as InfoIcon } from '@/assets/icons/info-icon.svg'
import { ReactComponent as SuccessIcon } from '@/assets/icons/success-icon.svg'
import { Link } from 'react-router-dom'
import AuthService from '@/app-domains/user/auth/Auth.service'
import ToastService from '@/components/Toast/Toast.service'
import { Tooltip } from '@/components/Tooltip'
import sessionService from '@/app-domains/user/session/session.service'
import commonsService from '@/services/commons.service'
import modalService from '@/services/modal.service'
import { BYODModal } from './BYODModal/BYODModal'

const premiumDomainsText = `
Premium domains have a cost every year. 
When you first sign up for Boxes and select a premium domain, you pay the first year of your premium domain in full. 
Boxes then adds 1 / 12 of the cost of your domain charge to your monthly service fee in order to pre-pay for your premium domain's fee next year. 
This prevents you from having to pay a large amount every year on your anniversary date. If you cancel your Boxes service before your anniversary date, we will fully refund your pre-payment of the premium domain charges for that year
`

export function SignUpThirdStep () {
  const [domain, setDomain] = useState('')
  const [TLDS, setTLDS] = useState([])
  const [TLDSOptions, setTLDSOptions] = useState([])
  const [selectedTLD, setSelectedTLD] = useState({})
  const [premiumDomainSelected, setPremiumDomainSelected] = useState(false)
  const [pricing, setPricing] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [discount, setDiscount] = useState({ percentage: 0 })
  const [couponCode, setCouponCode] = useState('')
  const [isCouponApplied, setIsCouponApplied] = useState(false)
  const [isDomainAvailable, setIsDomainAvailable] = useState(false)
  const [hasCoupon, setHasCoupon] = useState(false)
  const [suggestions, setSuggestions] = useState([])
  const debouncedAvailabilityCheck = useCallback(debounce(checkDomainAvailability, 1000), [])

  async function checkDomainAvailability (domainName) {
    if (domainName.length === 0) return
    
    const domainBeforeTLD = domainName.split('.')[0]
    // Safari doesn't support lookbehind operator neither lookahead, that's why we check the first and last chars manually
    if (!(
      /^([A-Za-z0-9-]{1,63})$/.test(domainBeforeTLD) &&
      domainBeforeTLD[0] !== '-' &&
      domainBeforeTLD[domainBeforeTLD.length - 1] !== '-'
    )) {
      return ToastService.show({
        type: 'error',
        title: 'Error',
        message: 'Domain has an invalid format',
      })
    }
    setSubmitting(true)
    ToastService.show({
      type: 'info',
      title: 'Availability',
      message: 'Checking domain availability ... ⏲',
    })
    const { available, suggestions } = await SignUpService.checkIfDomainIsAvailable(domainName)
    setIsDomainAvailable(available)
    suggestions && setSuggestions(suggestions)
    if (available) {
      setTimeout(() => {
        store.commit('START_SIDE_MENU:VISIBILITY', false)
      }, 0)
      ToastService.show({
        type: 'success',
        title: 'Congratulations!',
        message: `Your domain '${domainName}' is available!`,
      })
    } else {
      setTimeout(() => {
        store.commit('START_SIDE_MENU:VISIBILITY', true)
      }, 0)
      ToastService.show({
        type: 'error',
        title: 'We are sorry',
        message: 'That domain is not available at the moment',
      })
    }
    setSubmitting(false)
  }
  useEffect(() => {
    const preExistingCoupon = getItemFromLocalStorage('couponCode')
    const topDomainsArray = ['.com', '.im', '.info', '.life', '.link', '.me', '.name', '.net', '.world']
    if (preExistingCoupon) {
      validateCoupon(preExistingCoupon)
      setCouponCode(preExistingCoupon)
    }

    SignUpService.getTLDS().then((tlds) => {
      const freeTLDS = tlds.filter((tld) => tld.registrationPrice === 0).sort(arrangeDomainsInAlphabeticalOrder)
      const pricedTLDS = tlds.filter((tld) => tld.registrationPrice !== 0).sort(arrangeDomainsInPricingOrder)
      const topDomains = tlds.filter((tld) => topDomainsArray.indexOf(tld.tld) !== -1)
      setTLDSOptions([
        {
          type: 'group', name: 'Favorite domains', items: topDomains.map(tld => ({ value: tld.tld, label: `${tld.tld} - ${getPriceLabel(tld.registrationPrice / 100)}${tld.registrationPrice === 0 ? '' : '/yr'}` }))
        }, {
          type: 'group', name: 'Free domains', items: freeTLDS.map(tld => ({ value: tld.tld, label: `${tld.tld} - ${getPriceLabel(tld.registrationPrice / 100)}${tld.registrationPrice === 0 ? '' : '/yr'}` })),
        }, {
          type: 'group', name: 'Premium domains', items: pricedTLDS.map(tld => ({ value: tld.tld, label: `${tld.tld} - ${getPriceLabel(tld.registrationPrice / 100)}${tld.registrationPrice === 0 ? '' : '/yr'}` }))
        }
      ])

      tlds = Array.from(new Set([...topDomains, ...pricedTLDS, ...freeTLDS]))
      tlds = tlds.map(tld => (
        {
          registrationPrice: (tld.registrationPrice / 100),
          oneTimeCharge: (tld.oneTimeCharge / 100),
          tld: tld.tld,
          value: tld.tld,
          label: `${tld.tld} - ${getPriceLabel(tld.registrationPrice / 100)}${tld.registrationPrice === 0 ? '' : '/yr'}`
        }
      ))
      setTLDS(tlds)
      setSelectedTLD(tlds[0] || {})
    })
  }, [])

  const handleDomainChange = (value) => {
    if (!(/^[A-Za-z0-9-]{0,63}$/.test(value))) {
      return
    }

    setDomain(value)

    if (value.length === 0) {
      setIsDomainAvailable(false)
      setPricing({})
      debouncedAvailabilityCheck('')
      return
    }
    const prices = SignUpService.getPricings(selectedTLD.registrationPrice)
    setPricing(prices)

    const completeDomain = value + selectedTLD.tld
    debouncedAvailabilityCheck(completeDomain)
  }

  const travelToFreeTrialPeriodSection = () => {
    saveItemInLocalStorage('isfreetrial', true)
    commonsService.goToPage('/auth/signup/choose-subdomain')
  }

  const isPremiumDomain = (tld) => {
    return tld?.registrationPrice > 0
  }

  const handleTLDChange = (value) => {
    const selectedTld = TLDS.find(tld => tld.tld === value)
    const prices = SignUpService.getPricings(selectedTld.registrationPrice)
    setSelectedTLD(selectedTld)
    setPricing(prices)

    setPremiumDomainSelected(isPremiumDomain(selectedTld))
    if (domain.length === 0) { return }
    const completeDomain = domain + selectedTld.tld
    debouncedAvailabilityCheck(completeDomain)
  }

  const getCouponSection = () => {
    return (
      <div className='enter-coupon-section'>
        <InputMask label='Promo code' size='small' value={couponCode} onInput={(value) => { setCouponCode(value) }} />
        <span className='apply-coupon'><Button content="Apply" size="medium" disabled={submitting || (couponCode?.length === 0)} click={() => couponCode?.length > 0 && validateCoupon(couponCode, true) }/></span>
      </div>
    )
  }

  const validateCoupon = async (couponCode, couponManuallyInputed = false) => {
    setSubmitting(true)
    const result = await SignUpService.validateCoupon(couponCode)
    if (result.scope === 'ONLY_INVITATIONAL') {
      if (!couponManuallyInputed) return
      return ToastService.show({
        type: 'info',
        title: 'Promo code applied',
        message: 'Coupon doesn\'t have any discount associated'
      })
    }

    if (result.type === 'percentage' || result.type === 'fixed') {
      setIsCouponApplied(true)
      setDiscount(result)
      setSubmitting(false)
    } else {
      setSubmitting(false)
      ToastService.show({
        type: 'error',
        title: 'Error',
        message: 'Promotional or Gift Card code doesn\'t exist'
      })
    }
  }

  const checkDomainValue = () => {
    if (!domain) {
      ToastService.show({
        type: 'error',
        title: 'Error',
        message: 'Domain is empty'
      })
      setSubmitting(false)
    }
    return !!domain
  }

  const onSubmitHandler = (e) => {
    if (submitting) return e.preventDefault()
    if (!checkDomainValue()) return

    setSubmitting(true)
    const completeDomain = domain + selectedTLD.tld
    SignUpService.checkIfDomainIsAvailable(completeDomain)
      .then(() => {
        if (isCouponApplied) {
          SignUpService.orderDomainWithCoupon(completeDomain, couponCode)
        } else {
          SignUpService.orderDomain(completeDomain)
        }
      })
      .catch(() => {
        setIsDomainAvailable(false)
        setSubmitting(false)
      })
      .finally(setSubmitting(false))
    e?.preventDefault()
  }

  function getCouponMessage () {
    if (!discount?.type) return
    if (discount.type === 'fixed') { return `Coupon applied with ${discount.amount / 100}$ off!` }
    if (discount.scope === 'subscription_fee') {
      return `Coupon applied with ${discount.percentage}% off subscription fee!`
    } else {
      return `Coupon applied with ${discount.percentage}% off!`
    }
  }

  function getSummaryOffMessage () {
    if (!discount?.type) return
    if (discount.type === 'fixed') { return `${discount.amount / 100}$ off` }
    if (discount.scope === 'subscription_fee') {
      return `${discount.percentage}% off subscription fee`
    } else {
      return `${discount.percentage}% off`
    }
  }

  function arrangeDomainsInAlphabeticalOrder (a, b) {
    return b.tld - a.tld
  }

  function arrangeDomainsInPricingOrder (a, b) {
    return a.registrationPrice - b.registrationPrice
  }

  function getPriceLabel (price) {
    return price > 0 ? centsToDollars(price) : 'Free!'
  }

  function promptBYODModal () {
    modalService.open(<BYODModal/>)
  }

  return (
    <div className='domain-selection-parent'>
      {
        sessionService.getMe().me.status === 'trial_active'
          ? <Link className={`logout-link ${isDomainAvailable ? 'middle' : ''}`} onClick={() => commonsService.goToPage('/settings/billing')}>Go back to the app</Link>
          : <Link className={`logout-link ${isDomainAvailable ? 'middle' : ''}`} onClick={AuthService.signOut}>Log out</Link> }
      <div className={isDomainAvailable ? 'domain-selection-shrink' : 'domain-selection'}>
        <div className="domain-selection-title">Amazing, let's choose your personal domain!</div>
        <InputWithSelect onInput={handleDomainChange} onSelectChange={handleTLDChange} options={TLDSOptions} />
        { premiumDomainSelected && <div className='coupon-message'>
          <SimpleBanner type='info'>
            <p className='coupon-request-text'>
              Learn about premium domains <Tooltip text={premiumDomainsText} size='large'/>
            </p>
          </SimpleBanner>
        </div> }
        { sessionService.getMe().me.status === 'onboarding' && <div className="free-trial-request">
          <a onClick={travelToFreeTrialPeriodSection}>I want to enjoy a free trial period</a>
          </div> }
        <div className="free-trial-request">
          <a onClick={promptBYODModal}>I already have my domain and I want to use it with Boxes</a>
        </div>
        {suggestions.length > 0 &&
          <div className='suggestions-message'>
            <SimpleBanner type='warning'>
              <div>
                <p style={{ marginBottom: '16px' }}>
                  <p style={{ fontWeight: '500', marginBottom: '8px' }}>Unfortunately, that domain is not available at the moment.</p>
                  <p style={{ fontWeight: '300', fontSize: '14px', marginBottom: '12.5px' }}><i>Why don't you try choosing another TLD from the list?</i></p>
                  <p>In case you need some inspiration, we found some available alternatives:</p>
                </p>
                <ul style={{ listStyle: 'initial', marginLeft: '16px' }}>
                  {suggestions.map((suggestion) => <li key={suggestion}>{suggestion}</li>)}
                </ul>
              </div>
            </SimpleBanner>
          </div>
        }
        
        { !isCouponApplied &&
          <>
            { !hasCoupon && <div className='coupon-message'>
              <SimpleBanner type='info'>
                <p className='coupon-request-text'>
                  <InfoIcon className='coupon-request-text-icon'/>
                  <span>Do you have a coupon or promo code? click</span>
                  <span className="coupon-request-text-link" onClick={() => setHasCoupon(true)}>
                    here
                  </span>
                </p>
              </SimpleBanner>
              </div> }
            { hasCoupon && getCouponSection() }
          </>
        }

        { isCouponApplied &&
        <div className='coupon-message'>
          <SimpleBanner type='success'><p><SuccessIcon style={{ display: 'inline-block', position: 'relative', top: '-1px', marginRight: '8px' }}/>{getCouponMessage()}</p></SimpleBanner>
        </div>
      }

      </div>
      
      { isDomainAvailable &&
        <div className="domain-price">
          <div className="domain-price-title">Pricing {isCouponApplied && <i style={{
            fontSize: '16px'
          }}> ({getSummaryOffMessage()})</i>}</div>
          <div className="domain-price-list">
            <div className="domain-price-list-section">
              <span><b>One-time charge</b></span>
              <hr/>
              <p className="domain-price-item mt-xxxs flex justify-between items-center">
                <span>
                  <strong className='domain-selected-item'>{domain ? domain + selectedTLD.tld : selectedTLD.tld}</strong><br />
                  Domain purchase for 1 Year
                </span>
                <span className="domain-price-item-amount">{getPriceLabel(selectedTLD.registrationPrice)}</span>
              </p>
            </div>
            <div className="domain-price-list-section">
              <span><b>Monthly recurring charge</b></span>
              <hr/>
              <p className="domain-price-item">
                <span>Domain renewal monthly fee</span>
                <span className="domain-price-item-amount">{getPriceLabel(selectedTLD.registrationPrice / 12)}</span>
              </p>
              <p className="domain-price-item">
                <span>Boxes monthly subscription fee</span>
                <span className="domain-price-item-amount">{pricing.monthlySubscriptionFee && centsToDollars(pricing.monthlySubscriptionFee)}</span>
              </p>
            </div>
            <div className="domain-price-list-section">
              <span><b>To pay now:</b></span>
              <hr style={{ marginBottom: discount.type ? '10px' : '0px' }}/>
              { discount.type && <>
                <p className="domain-price-item">
                  <div className="domain-price-item-amount no-bold">Subtotal: <b>{getPriceLabel(pricing.oneTimeCharge, 'oneTime')}</b></div>
                </p>
                <hr style={{
                  width: '50%',
                  float: 'right',
                  clear: 'both',
                  marginTop: '10px',
                  marginBottom: '10px',
                }}/>
                <p className="domain-price-item">
                  <div className="domain-price-item-amount no-bold">Discount: <b>{centsToDollars(SignUpService.getDiscountAmount(pricing, discount))}</b></div>
                </p>
                <hr style={{
                  width: '50%',
                  float: 'right',
                  clear: 'both',
                  marginTop: '10px',
                  marginBottom: '10px',
                }}/>
                </>
              }
              <p className="domain-price-item">
                <div className="domain-price-item-amount">{discount.type ? 'Total:' : ''} {pricing.oneTimeCharge && getPriceLabel(SignUpService.getTotalAmount(pricing, discount))}</div>
              </p>

            </div>
            <div className="domain-price-list-section">
              <span><b>To pay in following months:</b></span>
              <hr/>
              { discount.scope === 'subscription_fee' && <>
                <p className="domain-price-item">
                  <div className="domain-price-item-amount no-bold">Subtotal: <b>{pricing.monthlySubscriptionFee && getPriceLabel(pricing.recurringCharge)}</b></div>
                </p>
                <hr style={{
                  width: '50%',
                  float: 'right',
                  clear: 'both',
                  marginTop: '10px',
                  marginBottom: '10px',
                }}/>
                <p className="domain-price-item">
                  <div className="domain-price-item-amount no-bold">Discount: <b>{centsToDollars(SignUpService.getDiscountAmountForSubscriptionFee(pricing, discount))}</b></div>
                </p>
                <hr style={{
                  width: '50%',
                  float: 'right',
                  clear: 'both',
                  marginTop: '10px',
                  marginBottom: '10px',
                }}/>
                </>
              }
              <p className="domain-price-item">
                <div className="domain-price-item-amount">{discount?.scope === 'subscription_fee' ? 'Total:' : ''} {pricing.recurringCharge && getPriceLabel(SignUpService.getRecurringAmount(pricing, discount))}</div>
              </p>
            </div>
          </div>
          <div className="submit-domain">
            { isDomainAvailable &&
              <Button
                size='large'
                content="Select domain"
                click={onSubmitHandler}
                disabled={submitting} />
            }
          </div>
        </div>
      }
    </div>
  )
}
